import React, { useRef, Children, cloneElement, isValidElement } from 'react';
import { motion, useInView, Variants } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface DrawingAnimationProps {
  children: React.ReactNode;
  duration?: number;
  delay?: number;
  stagger?: number;
  trigger?: 'inView' | 'immediate' | 'manual';
  isVisible?: boolean;
  once?: boolean;
  className?: string;
  onComplete?: () => void;
}

// Animation variants for SVG path drawing
const createDrawVariants = (duration: number, delay: number): Variants => ({
  hidden: {
    pathLength: 0,
    opacity: 0,
  },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: {
        duration,
        ease: [0.4, 0, 0.2, 1],
        delay,
      },
      opacity: {
        duration: 0.1,
        delay,
      },
    },
  },
});

// Wrapper variants for container fade
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

export const DrawingAnimation: React.FC<DrawingAnimationProps> = ({
  children,
  duration = 1.5,
  delay = 0,
  stagger = 0.1,
  trigger = 'inView',
  isVisible = true,
  once = true,
  className,
  onComplete,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once, margin: '-50px' });

  const shouldAnimate =
    trigger === 'immediate'
      ? true
      : trigger === 'inView'
        ? inView
        : isVisible;

  // Process SVG children to add drawing animation
  const processChildren = (children: React.ReactNode, pathIndex = { current: 0 }): React.ReactNode => {
    return Children.map(children, (child) => {
      if (!isValidElement(child)) return child;

      // Handle SVG element
      if (child.type === 'svg') {
        return cloneElement(child, {
          ...child.props,
          children: processChildren(child.props.children, pathIndex),
        });
      }

      // Handle path, circle, rect, line, polyline, polygon elements
      const animatableElements = ['path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse'];
      if (animatableElements.includes(child.type as string)) {
        const currentDelay = delay + pathIndex.current * stagger;
        pathIndex.current++;

        return (
          <motion.path
            {...child.props}
            as={child.type}
            variants={createDrawVariants(duration, currentDelay)}
            initial="hidden"
            animate={shouldAnimate ? 'visible' : 'hidden'}
            onAnimationComplete={
              pathIndex.current === 1 ? onComplete : undefined
            }
            style={{
              ...child.props.style,
              strokeDasharray: 1,
              strokeDashoffset: 1,
            }}
          />
        );
      }

      // Handle g (group) elements
      if (child.type === 'g') {
        return cloneElement(child, {
          ...child.props,
          children: processChildren(child.props.children, pathIndex),
        });
      }

      // For other elements with children
      if (child.props.children) {
        return cloneElement(child, {
          ...child.props,
          children: processChildren(child.props.children, pathIndex),
        });
      }

      return child;
    });
  };

  return (
    <motion.div
      ref={ref}
      className={cn(className)}
      variants={containerVariants}
      initial="hidden"
      animate={shouldAnimate ? 'visible' : 'hidden'}
    >
      {processChildren(children)}
    </motion.div>
  );
};

// Simpler version that just animates opacity and draws a wrapper
export const SketchReveal: React.FC<{
  children: React.ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
}> = ({ children, className, delay = 0, duration = 0.6 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className={cn(className)}
      initial={{ opacity: 0, scale: 0.95, rotate: -1 }}
      animate={
        inView
          ? { opacity: 1, scale: 1, rotate: 0 }
          : { opacity: 0, scale: 0.95, rotate: -1 }
      }
      transition={{
        duration,
        delay,
        ease: [0.4, 0, 0.2, 1],
      }}
    >
      {children}
    </motion.div>
  );
};

// Staggered reveal for multiple items
export const StaggeredSketchReveal: React.FC<{
  children: React.ReactNode[];
  className?: string;
  staggerDelay?: number;
  itemClassName?: string;
}> = ({ children, className, staggerDelay = 0.1, itemClassName }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20, rotate: -2 },
    visible: {
      opacity: 1,
      y: 0,
      rotate: 0,
      transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <motion.div
      ref={ref}
      className={cn(className)}
      variants={containerVariants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
    >
      {Children.map(children, (child, index) => (
        <motion.div key={index} className={itemClassName} variants={itemVariants}>
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
};

export default DrawingAnimation;
